import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Eye,
  Calendar,
  DollarSign,
  Package,
  Hammer
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui';

interface ProductWithBids {
  id: string;
  name: string;
  price: number;
  images: string[];
  seller: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  bidCount: number;
  pendingBids: number;
  highestBid: number;
  lowestBid: number;
  avgBidPrice: number;
  latestBidDate: string;
}

interface ProductsWithBidsResponse {
  products: ProductWithBids[];
  total: number;
  totalPages: number;
  page: number;
}

interface BidStatistics {
  totalProducts: number;
  totalBids: number;
  pendingBids: number;
  acceptedBids: number;
  rejectedBids: number;
  todayBids: number;
  avgBidPrice: number;
  acceptanceRate: string;
  topProducts: Array<{
    productName: string;
    bidCount: number;
  }>;
}

const Bidding: React.FC = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState<ProductWithBids[]>([]);
  const [statistics, setStatistics] = useState<BidStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchProductsWithBids = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...(searchTerm && { search: searchTerm }),
      });

      const response = await api.get(`/admin/products/with-offers?${params}`);
      const data: ProductsWithBidsResponse = response.data.data;

      type ApiProduct = ProductWithBids & {
        offerCount?: number;
        pendingOffers?: number;
        highestOffer?: number;
        lowestOffer?: number;
        avgOfferAmount?: number;
        latestOfferDate?: string;
      };

      const mapped = ((data.products || []) as ApiProduct[]).map((p): ProductWithBids => ({
        ...p,
        bidCount: p.offerCount ?? p.bidCount ?? 0,
        pendingBids: p.pendingOffers ?? p.pendingBids ?? 0,
        highestBid: p.highestOffer ?? p.highestBid ?? 0,
        lowestBid: p.lowestOffer ?? p.lowestBid ?? 0,
        avgBidPrice: p.avgOfferAmount ?? p.avgBidPrice ?? 0,
        latestBidDate: String(p.latestOfferDate ?? p.latestBidDate ?? ''),
      }));

      setProducts(mapped);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Error fetching products with bids:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  const fetchStatistics = async () => {
    try {
      setStatsLoading(true);
      const response = await api.get('/admin/offers/statistics');
      const raw = response.data.data;
      setStatistics({
        ...raw,
        totalBids: raw.totalOffers ?? raw.totalBids ?? 0,
        pendingBids: raw.pendingOffers ?? raw.pendingBids ?? 0,
        acceptedBids: raw.acceptedOffers ?? raw.acceptedBids ?? 0,
        rejectedBids: raw.rejectedOffers ?? raw.rejectedBids ?? 0,
        todayBids: raw.todayOffers ?? raw.todayBids ?? 0,
        avgBidPrice: raw.avgOfferAmount ?? raw.avgBidPrice ?? 0,
        topProducts: (raw.topProducts || []).map((p: { productName: string; offerCount?: number; bidCount?: number }) => ({
          productName: p.productName,
          bidCount: p.offerCount ?? p.bidCount ?? 0,
        })),
      });
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchProductsWithBids();
  }, [currentPage, searchTerm, fetchProductsWithBids]);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text: string, maxLength: number = 30) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{t('title', { ns: 'bidding' })}</h1>
        <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2">{t('subtitle', { ns: 'bidding' })}</p>
      </div>

      {/* Statistics Cards */}
      {!statsLoading && statistics && (
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
          <div className="bg-white rounded-lg shadow p-3 md:p-6">
            <div className="flex items-center">
              <div className="p-1.5 md:p-2 bg-blue-100 rounded-lg">
                <Package className="h-4 w-4 md:h-6 md:w-6 text-blue-600" />
              </div>
              <div className="ml-2 md:ml-4">
                <p className="text-xs md:text-sm font-medium text-gray-600">{t('stats.productsWithBids', { ns: 'bidding' })}</p>
                <p className="text-lg md:text-2xl font-bold text-gray-900">{statistics.totalProducts}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-3 md:p-6">
            <div className="flex items-center">
              <div className="p-1.5 md:p-2 bg-green-100 rounded-lg">
                <Hammer className="h-4 w-4 md:h-6 md:w-6 text-green-600" />
              </div>
              <div className="ml-2 md:ml-4">
                <p className="text-xs md:text-sm font-medium text-gray-600">{t('stats.totalBids', { ns: 'bidding' })}</p>
                <p className="text-lg md:text-2xl font-bold text-gray-900">{statistics.totalBids}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-3 md:p-6">
            <div className="flex items-center">
              <div className="p-1.5 md:p-2 bg-yellow-100 rounded-lg">
                <Calendar className="h-4 w-4 md:h-6 md:w-6 text-yellow-600" />
              </div>
              <div className="ml-2 md:ml-4">
                <p className="text-xs md:text-sm font-medium text-gray-600">{t('stats.pendingBids', { ns: 'bidding' })}</p>
                <p className="text-lg md:text-2xl font-bold text-gray-900">{statistics.pendingBids}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-3 md:p-6">
            <div className="flex items-center">
              <div className="p-1.5 md:p-2 bg-purple-100 rounded-lg">
                <DollarSign className="h-4 w-4 md:h-6 md:w-6 text-purple-600" />
              </div>
              <div className="ml-2 md:ml-4">
                <p className="text-xs md:text-sm font-medium text-gray-600">{t('stats.avgBidPrice', { ns: 'bidding' })}</p>
                <p className="text-lg md:text-2xl font-bold text-gray-900">{formatCurrency(statistics.avgBidPrice)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 md:p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder={t('search', { ns: 'bidding' })}
                className="pl-10 pr-4 py-2 w-full text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Products with Bids Table - Desktop */}
      <div className="bg-white rounded-lg shadow overflow-hidden hidden lg:block">
        <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200">
          <h3 className="text-base md:text-lg font-medium text-gray-900">
            {t('table.title', { ns: 'bidding' })} ({total})
          </h3>
        </div>

        {loading ? (
          <div className="p-6 text-center">{t('table.loading', { ns: 'common' })}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 table-fixed">
              <colgroup>
                <col className="w-[30%]" />
                <col className="w-[20%]" />
                <col className="w-[15%]" />
                <col className="w-[20%]" />
                <col className="w-[10%]" />
                <col className="w-[5%]" />
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('table.product', { ns: 'bidding' })}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('table.seller', { ns: 'bidding' })}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('table.bids', { ns: 'bidding' })}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('table.priceRange', { ns: 'bidding' })}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('table.latest', { ns: 'bidding' })}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('table.actions', { ns: 'common' })}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="flex items-center">
                        <img
                          className="h-10 w-10 rounded-lg object-cover flex-shrink-0"
                          src={product.images[0] || '/placeholder-product.png'}
                          alt={product.name}
                        />
                        <div className="ml-3 min-w-0 flex-1">
                          <div 
                            className="text-sm font-medium text-gray-900 truncate"
                            title={product.name}
                          >
                            {truncateText(product.name, 20)}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {formatCurrency(product.price)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {truncateText(`${product.seller.firstName} ${product.seller.lastName}`, 15)}
                      </div>
                      <div 
                        className="text-xs text-gray-500 truncate"
                        title={product.seller.email}
                      >
                        {truncateText(product.seller.email, 15)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="text-sm font-bold text-gray-900">
                        {product.bidCount}
                      </div>
                      <div className="text-xs text-yellow-600">
                        {product.pendingBids} {t('table.pending', { ns: 'bidding' })}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-xs text-gray-900">
                        <div className="font-semibold text-green-600 mb-1">
                          {formatCurrency(product.highestBid)}
                        </div>
                        <div className="text-gray-500">
                          {formatCurrency(product.lowestBid)}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-xs text-gray-500">
                      {new Date(product.latestBidDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <Link
                        to={`/offers/product/${product.id}`}
                        className="text-blue-600 hover:text-blue-900"
                        title={t('table.viewOffers', { ns: 'bidding' })}
                      >
                        <Eye className="h-4 w-4 mx-auto" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 md:px-6 py-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-700">
                {t('table.page', { ns: 'common' })} {currentPage} {t('table.of', { ns: 'common' })} {totalPages}
              </div>
              <div className="flex space-x-2">
                <Button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                >
                  {t('table.previous', { ns: 'common' })}
                </Button>
                <Button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  size="sm"
                >
                  {t('table.next', { ns: 'common' })}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Products with Bids Cards - Mobile */}
      <div className="lg:hidden space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Package size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">{t('table.noData', { ns: 'common' })}</p>
          </div>
        ) : (
          products.map((product) => (
            <div key={product.id} className="bg-white rounded-lg shadow p-4 space-y-4">
              {/* Product Header */}
              <div className="flex items-start space-x-3">
                <img
                  className="h-16 w-16 rounded-lg object-cover flex-shrink-0"
                  src={product.images[0] || '/placeholder-product.png'}
                  alt={product.name}
                />
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-gray-900 truncate" title={product.name}>
                    {product.name}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Original: {formatCurrency(product.price)}
                  </p>
                </div>
                <Link
                  to={`/offers/product/${product.id}`}
                  className="text-blue-600 hover:text-blue-900 p-2"
                  title={t('table.viewOffers', { ns: 'bidding' })}
                >
                  <Eye size={20} />
                </Link>
              </div>

              {/* Seller Info */}
              <div className="border-t border-gray-200 pt-3">
                <p className="text-xs text-gray-500 mb-1">Seller</p>
                <p className="text-sm font-medium text-gray-900">
                  {product.seller.firstName} {product.seller.lastName}
                </p>
                <p className="text-xs text-gray-500 truncate" title={product.seller.email}>
                  {product.seller.email}
                </p>
              </div>

              {/* Bids Info */}
              <div className="grid grid-cols-2 gap-4 border-t border-gray-200 pt-3">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Total Bids</p>
                  <p className="text-lg font-bold text-gray-900">{product.bidCount}</p>
                  <p className="text-xs text-yellow-600 mt-1">
                    {product.pendingBids} pending
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Price Range</p>
                  <p className="text-sm font-semibold text-green-600">
                    {formatCurrency(product.highestBid)}
                  </p>
                  <p className="text-xs text-gray-500">
                    Low: {formatCurrency(product.lowestBid)}
                  </p>
                </div>
              </div>

              {/* Average & Latest */}
              <div className="grid grid-cols-2 gap-4 border-t border-gray-200 pt-3">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Average Bid</p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatCurrency(product.avgBidPrice)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Latest Bid</p>
                  <p className="text-sm text-gray-700">
                    {formatDate(product.latestBidDate)}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}

        {/* Mobile Pagination */}
        {totalPages > 1 && (
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex flex-col items-center justify-between gap-4">
              <div className="text-sm text-gray-700 text-center">
                {t('table.page', { ns: 'common' })} {currentPage} {t('table.of', { ns: 'common' })} {totalPages}
              </div>
              <div className="flex space-x-2 w-full">
                <Button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                  className="flex-1"
                >
                  {t('table.previous', { ns: 'common' })}
                </Button>
                <Button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  className="flex-1"
                >
                  {t('table.next', { ns: 'common' })}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Bidding;